"use client";

import { useState } from "react";
import Image from "next/image";

import type { DemoFinetune } from "@/lib/demo-finetunes";
import PageContent from "@/components/page-content";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeading } from "./SectionHeading";
import { DetectionOverlay } from "./DetectionOverlay";
import { ImageLightbox } from "./ImageLightbox";

interface DetectionShowcaseProps {
  finetune: DemoFinetune;
}

function ShowcaseCard({
  imageSrc,
  imageAlt,
  imageWidth,
  imageHeight,
  overlay,
  label,
}: {
  imageSrc: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
  overlay: {
    gt: import("@/lib/demo-finetunes").DetectionBox[];
    predictions: import("@/lib/demo-finetunes").DetectionBox[];
  };
  label: string;
}) {
  const lightboxContent = (
    <div className="relative h-full w-full">
      <Image
        src={imageSrc}
        alt={imageAlt}
        fill
        sizes="100vw"
        className="object-contain"
      />
      <DetectionOverlay overlay={overlay} />
    </div>
  );

  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <ImageLightbox lightboxContent={lightboxContent}>
        <div className="relative aspect-[16/9] overflow-hidden rounded-lg bg-black/5 dark:bg-white/5">
          <Image
            src={imageSrc}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            sizes="(min-width: 1024px) 500px, 90vw"
            className="h-full w-full object-contain"
          />
          <DetectionOverlay overlay={overlay} />
        </div>
      </ImageLightbox>
    </div>
  );
}

export function DetectionShowcase({ finetune }: DetectionShowcaseProps) {
  const showcase = finetune.detectionShowcase;
  const [activeIndex, setActiveIndex] = useState(0);

  if (!showcase?.length) return null;

  const example = showcase[activeIndex];

  return (
    <PageContent>
      <section className="space-y-8">
        <SectionHeading
          eyebrow="Results"
          title="See it in action"
          description="Side-by-side comparisons on real game footage. Base model on the left, fine-tuned on the right."
        />

        {/* Dots */}
        <div className="flex items-center gap-2">
          {showcase.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveIndex(i)}
              aria-label={`Example ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === activeIndex
                  ? "w-6 bg-foreground"
                  : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
            />
          ))}

          {/* Legend */}
          <div className="ml-auto flex items-center gap-5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-4 rounded bg-green-500" />
              Correct
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-4 rounded bg-red-500" />
              False positive
            </span>
          </div>
        </div>

        {/* Active example */}
        <Card className="overflow-hidden border-border/60 bg-card/80 shadow-sm">
          <CardContent className="p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <ShowcaseCard
                imageSrc={example.image.src}
                imageAlt={example.image.alt}
                imageWidth={example.image.width}
                imageHeight={example.image.height}
                overlay={example.before}
                label="Base Moondream 3 Preview"
              />
              <ShowcaseCard
                imageSrc={example.image.src}
                imageAlt={example.image.alt}
                imageWidth={example.image.width}
                imageHeight={example.image.height}
                overlay={example.after}
                label="Fine-tuned Moondream 3 Preview"
              />
            </div>
          </CardContent>
        </Card>
      </section>
    </PageContent>
  );
}
